import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { LogoComponent } from '../../components/logo/logo.component';
import { PartnerService } from '../../services/partner.service';

/** Same rule as the backend (PartnerSetPassword). */
export const PARTNER_PASSWORD_MIN_LENGTH = 8;

export function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  return group.get('password')?.value === group.get('confirm')?.value
    ? null
    : { passwordsNotMatching: true };
}

/**
 * /partner/set-password?token=… from the mailed link: sets (or resets) the partner
 * area's password and logs in. The link works once.
 */
@Component({
  selector: 'app-partner-set-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputGroupImports,
    HlmSpinnerImports,
    LogoComponent,
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './partner-set-password.component.html',
})
export class PartnerSetPasswordComponent {
  private readonly partnerService = inject(PartnerService);
  private readonly router = inject(Router);

  readonly token = inject(ActivatedRoute).snapshot.queryParamMap.get('token');
  readonly minLength = PARTNER_PASSWORD_MIN_LENGTH;
  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(
    this.token ? null : 'Der Link ist unvollständig. Fordere einfach einen neuen an.',
  );
  readonly showPassword = signal(false);

  readonly form = inject(FormBuilder).nonNullable.group(
    {
      password: ['', [Validators.required, Validators.minLength(PARTNER_PASSWORD_MIN_LENGTH)]],
      confirm: ['', Validators.required],
    },
    { validators: passwordsMatch },
  );

  get passwordInvalid(): boolean {
    return this.submitted() && this.form.controls.password.invalid;
  }

  get confirmInvalid(): boolean {
    return (
      this.submitted() &&
      (this.form.controls.confirm.invalid || !!this.form.errors?.['passwordsNotMatching'])
    );
  }

  submit(): void {
    if (this.loading() || !this.token) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.form.invalid) return;

    this.loading.set(true);
    this.partnerService.setPassword(this.token, this.form.getRawValue().password).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/partner/clients']);
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(
          err.status === 400
            ? (err.error?.detail ?? 'Der Link ist ungültig oder abgelaufen.')
            : 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.',
        );
      },
    });
  }
}
